import { Box, Typography, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import FolderCard from './Cards/FolderCard';
import FileCard from './Cards/FileCard';
import TextCard from './Cards/TextCard';
import UrlCard from './Cards/UrlCard';
import { FileServiceModule, ServiceModule, ServiceModuleTypes, TextServiceModule, UrlServiceModule } from '../../../types/serviceModuleTypes';
import AddIcon from "@mui/icons-material/Add";
import { useAppDispatch } from '../../../hooks/StoreHooks';
import { deleteServiceModuleThunk, fetchServiceModulesThunk } from '../../../store/thunk/serviceModulesThunk';
import { useNewestServiceModule, useServiceModules } from '../../../store/selectors/serviceModulesSelector';
import { useNavigate } from 'react-router-dom';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { DividerLine, ModuleItemBox, StyledFolderContainer } from './FolderModule.styles';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../../hooks/UseLocale';
import { resetNewestServiceModule } from '../../../store/reducers/serviceModulesReducer';
import ServiceModuleCreator from '../ServiceModuleCreator';
import ServiceModuleEditor from '../ServiceModuleEditor';
import { theme } from '../../../theme';
import CalendarCard from './Cards/CalendarCard';
import { StyledAddButton } from '../ServiceModules.styles';

type Props = {
    folderId: number;
};

type RenderModuleProps = {
    mod: ServiceModule;
    onDelete: (id: number) => void;
    onEdit: (mod: ServiceModule) => void;
};

const RenderModule = ({ mod, onDelete, onEdit }: RenderModuleProps) => {
    const commonProps = {
        id: mod.id,
        type: mod.type,
        name: mod.name,
        iconName: mod.iconName,
        iconUrl: mod.iconUrl,
        colorHex: mod.colorHex ?? theme.palette.primary.main,
        onDelete: () => onDelete(mod.id),
        onEdit: () => onEdit(mod),
    };

    switch (mod.type) {
        case ServiceModuleTypes.File:
            return (
                <FileCard
                    {...commonProps}
                    fileUrl={(mod as FileServiceModule).downloadUrl ?? (mod as FileServiceModule).path}
                    filename={(mod as FileServiceModule).filename}
                />
            );
        case ServiceModuleTypes.Text:
            return (
                <TextCard
                    {...commonProps}
                    text={(mod as TextServiceModule).text}
                />
            );
        case ServiceModuleTypes.Url:
            return (
                <UrlCard
                    {...commonProps}
                    url={(mod as UrlServiceModule).url}
                />
            );
        case ServiceModuleTypes.Folder:
            return (
                <FolderCard
                    {...commonProps}
                />
            );
        case ServiceModuleTypes.Calendar:
            return (
                <CalendarCard
                    {...commonProps}
                />
            );
        default:
            return null;
    }
};

const FolderModule = ({ folderId }: Props) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch();
    const modules = useServiceModules();
    const navigate = useNavigate();
    const locale = useLocale();
    const newestServiceModule = useNewestServiceModule()
    const currentFolder = modules.find(mod => mod.id === folderId);
    const parentId = currentFolder?.parentFolderServiceModuleId;
    const [editingModule, setEditingModule] = useState<ServiceModule | null>(null);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleTogglePopper = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl((prev) => (prev ? null : event.currentTarget))
    }

    const handleClosePopper = () => setAnchorEl(null)

    useEffect(() => {
        dispatch(fetchServiceModulesThunk());
    }, [dispatch]);

    useEffect(() => {
        if (newestServiceModule) {
            setEditingModule(newestServiceModule);
            dispatch(resetNewestServiceModule());
        }
    }, [newestServiceModule, dispatch]);

    const children: ServiceModule[] = modules
        .filter((mod) => mod.parentFolderServiceModuleId === folderId)
        .map((mod) => ({
            ...mod,
            name: mod.name ?? "",
            iconName: mod.iconName ?? 'document',
        }))
        .sort((a, b) => {
            const typeOrder = {
                [ServiceModuleTypes.Folder]: 0,
                [ServiceModuleTypes.File]: 1,
                [ServiceModuleTypes.Text]: 2,
                [ServiceModuleTypes.Url]: 3,
                [ServiceModuleTypes.Calendar]: 4
            };
            const typeCompare = typeOrder[a.type] - typeOrder[b.type];
            return typeCompare !== 0 ? typeCompare : a.name.localeCompare(b.name);
        });

    const handleEdit = (mod: ServiceModule) => {
        setEditingModule(mod);
    };

    const handleCloseModal = () => {
        setEditingModule(null);
    };

    const handleDelete = async (id: number) => {
        const confirmDelete = window.confirm(t("serviceModulesPage.folderServicePage.deleteConfirmation"));
        if (confirmDelete) {
            await dispatch(deleteServiceModuleThunk(id));
            setEditingModule(null);
        }
    };

    const renderMoveUp = () => (
        <ModuleItemBox
            onClick={() => {
                if (parentId) {
                    navigate(`/${locale}/servicemodules/folder/${parentId}`);
                } else {
                    navigate(`/${locale}/servicemodules/`);
                }
            }}
        >
            <KeyboardArrowUpIcon color="primary" />
            <Typography sx={{ flexGrow: 1, marginLeft: 2 }}>
                {t("serviceModulesPage.folderServicePage.moveUp")}
            </Typography>
        </ModuleItemBox>
    );

    return (
        <StyledFolderContainer>
            <Typography variant="h4" sx={{ mb: 2 }}>
                {t("serviceModulesPage.folderServicePage.folderContents")}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
                {t("serviceModulesPage.folderServicePage.folderItemsCount")} {children.length}
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Tooltip title={t("buttons.addItem")} arrow>
                    <StyledAddButton
                        variant="outlined"
                        onClick={handleTogglePopper}
                        startIcon={<AddIcon />}
                        aria-label={t("buttons.addItem")}
                    />
                </Tooltip>
            </Box>

            <ServiceModuleCreator anchorEl={anchorEl} parentFolderId={currentFolder?.id} onClose={handleClosePopper} />

            <Box sx={{ mt: '16px' }}>
                {renderMoveUp()}
                <DividerLine />
                {children.map((mod, idx) => (
                    <Box key={mod.id}>
                        <RenderModule
                            mod={mod}
                            onDelete={handleDelete}
                            onEdit={handleEdit}
                        />
                        {idx !== children.length - 1 && <DividerLine />}
                    </Box>
                ))}
            </Box>

            {editingModule && (
                <ServiceModuleEditor
                    module={editingModule}
                    onClose={handleCloseModal}
                    onDelete={handleDelete}
                />
            )}
        </StyledFolderContainer>
    );
};

export default FolderModule;
